import {
  getChartsAccount,
  getChartsAccountSelection,
  setAccountTypeSelection,
  setJournalLines,
  setOperationalAccountMaps,
  setPaymentTerms,
  setPaymentTermsSelection,
} from "../slice/accountSlice";
import { apiSlice } from "./apiSlice";

export const accountApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createChartsAccount: builder.mutation({
      query: data => ({ url: "/api_account/account/", method: "post", body: data }),
      invalidatesTags: (result, error) => (error ? [] : ["ChartsAccount"]),
    }),
    createPaymentTerm: builder.mutation({
      query: data => ({ url: "/api_account/payment_term/", method: "post", body: data }),
      invalidatesTags: (result, error) => (error ? [] : ["PaymentTerm"]),
    }),
    getChartsAccount: builder.query({
      query: (params = null) =>
        params ? `/api_account/account/?${params}` : "/api_account/account/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getChartsAccount(result.data))).catch(error => {});
      },
    }),

    getChartsAccountById: builder.query({
      query: id => ({
        url: `/api_account/account/${id}/`,
      }),
    }),
    getChartsAccountSelection: builder.query({
      providesTags: ["ChartsAccount"],
      query: (arg = null) => {
        const params = arg && typeof arg === "object" && "params" in arg ? arg.params : arg;
        return params
          ? `/api_account/account/selection/?${params}`
          : "/api_account/account/selection/";
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getChartsAccountSelection(result.data)))
          .catch(error => {});
      },
    }),
    getPaymentTerm: builder.query({
      query: (params = null) =>
        params ? `/api_account/payment_term/?${params}` : "/api_account/payment_term/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setPaymentTerms(result.data))).catch(error => {});
      },
    }),

    getPaymentTermById: builder.query({
      query: id => ({
        url: `/api_account/payment_term/${id}/`,
      }),
    }),
    getPaymentTermSelection: builder.query({
      providesTags: ["PaymentTerm"],
      query: () => "/api_account/payment_term/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPaymentTermsSelection(result.data)))
          .catch(error => {});
      },
    }),
    getJournalLine: builder.query({
      query: (params = null) =>
        params ? `/api_account/journal_line/?${params}` : "/api_account/journal_line/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setJournalLines(result.data))).catch(error => {});
      },
    }),
    getAccountCategorySelection: builder.query({
      query: () => "/api_account/account_category/",
    }),
    getTrackingCategorySelection: builder.query({
      query: () => "/api_account/tracking_category/",
    }),
    syncTrackingCategoriesWithExternalSystem: builder.query({
      query: () => "/api_account/sync_tracking_category_with_external_system/",
    }),
    getAccountType: builder.query({
      query: () => "/api_account/account_type/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setAccountTypeSelection(result.data)))
          .catch(error => {});
      },
    }),
    getCompanyOperationPreferences: builder.query({
      query: ({ id }) => `/api_company/opr_preference/${id}/`,
      providesTags: ["Organization"],
    }),
    updateCompanyOperationPreferences: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api_company/opr_preference/${id}/`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: ["Organization"],
    }),

    updateChartsAccount: builder.mutation({
      query: ({ data, id }) => ({
        url: `/api_account/account/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["ChartsAccount"]),
    }),
    updatePaymentTerm: builder.mutation({
      query: ({ data, id }) => ({
        url: `/api_account/payment_term/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PaymentTerm"]),
    }),
    getAccountMapping: builder.query({
      query: () => "/api_company/account_mapping/",
      providesTags: ["OperationalAccountMapping"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
          const transformedData = transformAccountMappingToObject(result.data);
          dispatch(setOperationalAccountMaps(transformedData));
        } catch (error) {
          // Leave Redux maps unchanged; page shows isError + retry.
          // Avoid silent empty catch so failed loads stay visible in RTK state.
          console.warn("getAccountMapping failed", error);
        }
      },
    }),
    createAccountMapping: builder.mutation({
      query: data => ({
        url: "/api_company/account_mapping/",
        method: "POST",
        body: data,
      }),
      // After a successful save, invalidate so list/detail stay in sync.
      invalidatesTags: ["OperationalAccountMapping"],
    }),
    getOrganization: builder.query({
      query: () => "/api_company/organization/",
      providesTags: ["Organization"],
    }),
    updateOrganization: builder.mutation({
      query: data => ({
        url: "/api_company/organization/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Organization", "Onboarding"],
    }),
    getDataResetCurrent: builder.query({
      // Catch-up snapshot only. Live lock uses WS; do not set pollingInterval.
      query: () => "/api_company/data_reset/current/",
    }),
    startDataReset: builder.mutation({
      query: data => ({
        url: "/api_company/data_reset/",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(apiSlice.util.updateQueryData("getDataResetCurrent", undefined, () => data));
        } catch (_error) {
          // The clear was refused. The row shows the error from the response.
        }
      },
    }),
  }),
});

const transformAccountMappingToObject = data => {
  const result = {};
  if (Array.isArray(data)) {
    data.forEach(item => {
      result[item.acc_map_def.event_key] = item;
    });
  }
  return result;
};

export const {
  useCreateChartsAccountMutation,
  useGetChartsAccountByIdQuery,
  useGetChartsAccountQuery,
  useGetAccountCategorySelectionQuery,
  useGetAccountTypeQuery,
  useGetTrackingCategorySelectionQuery,
  useLazySyncTrackingCategoriesWithExternalSystemQuery,
  useLazyGetAccountTypeQuery,
  useLazyGetChartsAccountSelectionQuery,
  useGetChartsAccountSelectionQuery,
  useLazyGetAccountCategorySelectionQuery,
  useCreatePaymentTermMutation,
  useGetPaymentTermByIdQuery,
  useGetPaymentTermQuery,
  useGetPaymentTermSelectionQuery,
  useLazyGetPaymentTermSelectionQuery,
  useGetJournalLineQuery,
  useGetCompanyOperationPreferencesQuery,
  useUpdateChartsAccountMutation,
  useUpdatePaymentTermMutation,
  useUpdateCompanyOperationPreferencesMutation,
  useGetAccountMappingQuery,
  useCreateAccountMappingMutation,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useGetDataResetCurrentQuery,
  useStartDataResetMutation,
} = accountApiSlice;
